import { useEffect, useMemo, useState } from "react";
import type { Person, RoleDefinition, Skill } from "../services/api";
import { getPerson, listRoles, listSkills, updatePerson, updatePersonSkills } from "../services/api";
import { useAuth } from "../auth/AuthContext";
import { PersonForm } from "./PersonForm";
import { SkillsSelector } from "./SkillsSelector";
import { Modal } from "./Modal";
import { getErrorAlertMessage } from "../utils/errors";

export function MyProfilePage() {
  const { user } = useAuth();
  const primaryEmail = useMemo(() => (user?.email ?? user?.username ?? "").toLowerCase(), [user]);

  const [person, setPerson] = useState<Person | null>(null);
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [assigningSkills, setAssigningSkills] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function bootstrap() {
      if (!primaryEmail) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [roleData, skillData, personData] = await Promise.all([
          listRoles(),
          listSkills(),
          getPerson(primaryEmail),
        ]);
        setRoles(roleData);
        setSkills(skillData);
        setPerson(personData);
      } catch (err) {
        setError(getErrorAlertMessage("load your profile", err));
      } finally {
        setLoading(false);
      }
    }

    void bootstrap();
  }, [primaryEmail]);

  async function handleSubmit(updated: Person) {
    if (!person) return;
    setSaving(true);
    setError(null);
    try {
      const next = await updatePerson(person.email, updated);
      setPerson(next);
    } catch (err) {
      setError(getErrorAlertMessage("update your profile", err));
    } finally {
      setSaving(false);
    }
  }

  async function handleSkillSave(skillCodes: string[]) {
    if (!person) return;
    try {
      const next = await updatePersonSkills(person.email, skillCodes);
      setPerson(next);
      setAssigningSkills(false);
    } catch (err) {
      setError(getErrorAlertMessage("update your skills", err));
    }
  }

  if (!primaryEmail) {
    return (
      <div className="app-shell centered">
        <p>We could not determine your Cognito username. Contact an administrator.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="app-shell centered">
        <p>Loading your profile…</p>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="app-shell centered">
        <p>We couldn't find a person record for {primaryEmail}. Reach out to operations for assistance.</p>
      </div>
    );
  }

  return (
    <div className="layout single-column">
      {error && <div className="alert error">{error}</div>}
      <section className="panel">
        <div className="panel-header">
          <h2>My Profile</h2>
          <button type="button" className="ghost" onClick={() => setAssigningSkills(true)}>
            Manage Skills
          </button>
        </div>
        <PersonForm
          initial={person}
          availableRoles={roles}
          onSubmit={handleSubmit}
          onCancel={() => setPerson(person)}
          isSaving={saving}
        />
      </section>

      <Modal open={assigningSkills} onClose={() => setAssigningSkills(false)} title="Manage Skills">
        <SkillsSelector
          person={person}
          availableSkills={skills}
          onSave={handleSkillSave}
          onClose={() => setAssigningSkills(false)}
        />
      </Modal>
    </div>
  );
}
